import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { Box } from '@mui/material';

// comps
import DialogTemplate from '../../components/dialogs/dialogTemplate';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import GlobalDropdown from '../../components/global/dropdown';
import ToastMessage from '../../components/global/toastMessage';

// icons
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';
import GlobalRadioGroup, { GlobalRadioGroupOptions } from '../../components/global/radio';
import GlobalCheckbox from '../../components/global/checkbox';

export interface ItemListDataItem {
    id?: number;
    itemCategory: any;
    name: string;
    description: string;
    costPerItem: number;
    isPriceAdjustmentAllowed: boolean;
    isActive: boolean;
    pstRate: number;
    gstRate: number;
}

export interface ItemCategory {
    id?: number;
    name: string;
    code: string
}

interface ItemListDialogProps {
    data: ItemListDataItem;
    onSave: (item: ItemListDataItem) => void;
    onClose: () => void;
    itemCategories: any;
    onCreate: (item: ItemListDataItem) => void;
}

const ItemListDialog = (props: ItemListDialogProps) => {

    const { data, onSave, onClose, itemCategories, onCreate } = props;
    const isCreate = !data.id;

    const [errorMsg, setErrorMsg] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [isClicked, setIsClicked] = useState(false);

    const [dataItem, setDataItem] = useState(data);

    const onChange = (name: any, value: any) => {
        if (name === 'costPerItem') {
            if (value < 0) {
                toast.error("Price must be greater than 0");
                return;
            }
        }
        setDataItem({
            ...dataItem,
            [name]: value,
        });
    };


    useEffect(() => {
        const requiredFieldsArr = ['itemCategory', 'name'];
        setRequiredFields(requiredFieldsArr.filter(field => {
            if (typeof dataItem[field] === 'string' && dataItem[field].trim() === '') return true;

            if (typeof dataItem[field] === 'number' && isNaN(dataItem[field])) return true;

            if (field === 'itemCategory')
                return !dataItem['itemCategory']?.id;

            return (dataItem[field] === null || dataItem[field] === undefined);
        }));
    }, [dataItem]);

    const handleSave = () => {

        let item = { ...dataItem };

        setIsClicked((state) => !state);

        if (requiredFields.length === 0) {
            if (isCreate) onCreate(item);
            else {
                // If fields were NOT edited (= if the object is unchanged)
                if (_.isEqual(item, data)) {
                    toast.warning(`There were no changes made`);
                    onClose();
                } else {
                    onSave(item);
                }
            }
        } else {
            setErrorMsg(`The required fields must be filled in order to proceed.`);
        }
    };

    const handleErrorState = (name: string) => {
        if (isClicked) {
            return requiredFields?.includes(name);
        }
    };

    const pstRadioOptions: GlobalRadioGroupOptions[] = [
        { label: 'None', value: 0 },
        { label: '7%', value: 7 },
        { label: '10%', value: 10 }
    ];

    const gstRadioOptions: GlobalRadioGroupOptions[] = [
        { label: 'None', value: 0 },
        { label: '5%', value: 5 },
    ];

    const renderContent = () => {
        return (
            <Container>
                {errorMsg && (
                    <FlexBox>
                        <ToastMessage isError msg={errorMsg} icon={<WarningIcon />} />
                    </FlexBox>
                )}
                <FlexBox>
                    <InputBox sx={{ '& > *': { mr: '10px' } }}>
                        <Label>Category <span>*</span></Label>
                        <GlobalDropdown
                            isRequired
                            isError={handleErrorState('category')}
                            placeholder={isCreate ? 'Select Category' : ''}
                            data={itemCategories}
                            field={'id'}
                            name={'name'}
                            dataItemKey={'itemCategory'}
                            value={dataItem.itemCategory}
                            onChange={onChange}
                        />
                        <Label>Item <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('name')}
                            placeholder="Item Name"
                            value={dataItem.name}
                            name={'name'}
                            onChange={onChange}
                        />
                        <Label>Description</Label>
                        <GlobalInput
                            multiline
                            minRows={2}
                            placeholder="Item Description (Max 100 characters)"
                            value={dataItem.description}
                            name={'description'}
                            onChange={onChange}
                            sx={{ mb: '1em' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                            <div>
                                <Label>Cost Per Item <span>*</span></Label>
                                <GlobalInput
                                    isRequired
                                    isError={handleErrorState('costPerItem')}
                                    placeholder="$"
                                    value={dataItem.costPerItem}
                                    name={'costPerItem'}
                                    onChange={onChange}
                                    type={'number'}
                                    parse={'float'}
                                />
                            </div>

                            <GlobalCheckbox name={'isPriceAdjustmentAllowed'} onChange={onChange} label={'Allow Cost Adjustment'} isChecked={dataItem.isPriceAdjustmentAllowed} useCheckedValue={true} />
                            <GlobalCheckbox name={'isActive'} onChange={onChange} label={'Active'} isChecked={dataItem.isActive} useCheckedValue={true} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                            <div>
                                <Label>PST Rate</Label>
                                <GlobalRadioGroup onChange={onChange} name="pstRate" options={pstRadioOptions} type={'number'} checkedOptionValue={dataItem.pstRate} />
                            </div>

                            <div>
                                <Label>GST Rate</Label>
                                <GlobalRadioGroup onChange={onChange} name="gstRate" options={gstRadioOptions} type={'number'} checkedOptionValue={dataItem.gstRate} />
                            </div>
                        </Box>
                    </InputBox>
                </FlexBox>
                <FlexBox sx={{ mt: '2em' }}>
                    <GlobalButton text="cancel" variant="outlined" onClick={onClose} />
                    <GlobalButton text={isCreate ? 'create' : 'save'} variant="contained" onClick={handleSave} />
                </FlexBox>
            </Container>
        );
    };

    return (
        <>
            <DialogTemplate {...props} title={'Event Item'} renderContent={renderContent} />
        </>
    );
};

export default ItemListDialog;

const Container = styled(Box)`
    && {
        width: 100%;
        padding: 1em 0px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
`;

const InputBox = styled(Box)`
    && {
        width: 100%;
        & > .MuiBox-root {
            margin-bottom: 1em;
        }
    }
`;

const Label = styled.div`
    color: #0085E6;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 2px;

    & > span {
        color: #D73948;
    }
`
